import React from 'react';
import { Star, Quote } from 'lucide-react';

/**
 * Auto-rotating testimonial carousel.
 * - 3 cards visible on desktop, 2 on tablet, 1 on mobile (via responsive widths).
 * - Smooth CSS marquee animation defined in index.css (.testimonial-marquee).
 * - Pauses on hover (via group-hover).
 * - Cards rendered twice for seamless infinite loop.
 */
export default function TestimonialCarousel({ testimonials = [], bgGradientFrom = '#FAFAFA' }) {
  if (!testimonials.length) return null;
  const items = [...testimonials, ...testimonials];

  return (
    <div className="relative overflow-hidden group" data-testid="testimonial-carousel">
      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-12 sm:w-24 z-10"
        style={{ background: `linear-gradient(to right, ${bgGradientFrom}, transparent)` }}
      ></div>
      <div
        className="pointer-events-none absolute inset-y-0 right-0 w-12 sm:w-24 z-10"
        style={{ background: `linear-gradient(to left, ${bgGradientFrom}, transparent)` }}
      ></div>

      <div className="testimonial-marquee flex gap-5 py-2">
        {items.map((t, idx) => (
          <div
            key={`${t.testimonial_id || idx}-${idx}`}
            className="bg-white p-6 border border-[#E2E8F0] rounded-2xl shadow-sm hover:shadow-md transition-shadow flex-shrink-0 w-[300px] sm:w-[340px] lg:w-[360px] flex flex-col"
            data-testid={`testimonial-card-${idx % testimonials.length}`}
          >
            <Quote size={20} className="text-[#1D4ED8] opacity-30 mb-3" />
            <div className="flex gap-0.5 mb-3">
              {[...Array(t.rating || 5)].map((_, i) => (
                <Star key={i} size={16} className="text-[#F97316] fill-[#F97316]" />
              ))}
            </div>
            <p className="text-sm text-[#475569] mb-5 italic leading-relaxed flex-1">"{t.content || t.quote}"</p>
            {t.result_badge && (
              <div className="inline-block self-start text-[10px] font-bold tracking-wider uppercase text-[#1D4ED8] bg-[#EFF6FF] border border-[#DBEAFE] rounded-full px-2.5 py-1 mb-3">
                {t.result_badge}
              </div>
            )}
            <div>
              <div className="font-bold text-[#0A0F1C] text-sm">{t.client_name || t.name}</div>
              <div className="text-xs text-[#475569]">
                {t.role || t.title}{t.company && `, ${t.company}`}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
